import { MoneyOff } from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { applyEmail, applyName } from '../../helpers/captureMode';

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });
}

function SubscriptionBillingExemptionsPage() {
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [exemptions, setExemptions] = useState([]);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({
        user_uuid: '',
        reason: '',
        exempt_until: '',
    });

    const loadExemptions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/subscription-billing/exemptions');
            setExemptions(Array.isArray(response?.data?.data) ? response.data.data : []);
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Não foi possível carregar as isenções.', 'error');
            setExemptions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (cancelled || !user || typeof user.is_master !== 'boolean') {
                    return;
                }
                patchStoredUser({ is_master: user.is_master });
                setIsMaster(user.is_master);
            })
            .catch(() => setIsMaster(false))
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isMaster || checkingAccess) {
            return undefined;
        }
        loadExemptions();
        api.get('/users', { params: { page: 1, limit: 500 } })
            .then((response) => {
                const list = response?.data?.data || response?.data?.items || [];
                setUsers(Array.isArray(list) ? list : []);
            })
            .catch(() => setUsers([]));
        return undefined;
    }, [isMaster, checkingAccess, loadExemptions]);

    const userOptions = useMemo(() => (
        [...users]
            .sort((a, b) => String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''), 'pt-BR'))
    ), [users]);

    const handleCreate = async (event) => {
        event.preventDefault();
        if (!form.user_uuid) {
            Swal.fire('Atenção', 'Selecione um usuário.', 'warning');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                user_uuid: form.user_uuid,
                reason: form.reason.trim(),
            };
            if (form.exempt_until.trim()) {
                payload.exempt_until = form.exempt_until.trim();
            }
            const response = await api.post('/subscription-billing/exemptions', payload);
            const created = response?.data?.data;
            if (created?.uuid) {
                setExemptions((prev) => [created, ...prev.filter((item) => item.user_uuid !== created.user_uuid)]);
            } else {
                await loadExemptions();
            }
            setForm({ user_uuid: '', reason: '', exempt_until: '' });
            Swal.fire('Salvo', 'Isenção registrada. O usuário não aparecerá nas cobranças enquanto estiver ativa.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Não foi possível criar a isenção.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleRevoke = async (exemption) => {
        const confirmed = await Swal.fire({
            title: 'Encerrar isenção?',
            html: `O usuário <strong>${exemption.user_name || exemption.user_email}</strong> voltará a ser cobrado normalmente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Encerrar isenção',
            cancelButtonText: 'Cancelar',
        });
        if (!confirmed.isConfirmed) {
            return;
        }
        try {
            await api.delete(`/subscription-billing/exemptions/${exemption.uuid}`);
            setExemptions((prev) => prev.filter((item) => item.uuid !== exemption.uuid));
            Swal.fire('Encerrada', 'A isenção foi revogada.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Não foi possível encerrar a isenção.', 'error');
        }
    };

    if (checkingAccess) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin" underline="hover" color="inherit">
                    Admin
                </Link>
                <Typography color="text.primary">Isenções de cobrança</Typography>
            </Breadcrumbs>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <MoneyOff color="primary" />
                <Typography variant="h5" component="h1">
                    Isenções de cobrança
                </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
                Usuários isentos não aparecem na gestão de cobranças. Deixe a data em branco para isenção por tempo indeterminado.
                Você pode encerrar a isenção a qualquer momento para voltar a cobrar o usuário.
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Nova isenção
                </Typography>
                <Box component="form" onSubmit={handleCreate}>
                    <Stack spacing={2}>
                        <FormControl fullWidth required>
                            <InputLabel id="billing-exemption-user-label">Usuário</InputLabel>
                            <Select
                                labelId="billing-exemption-user-label"
                                label="Usuário"
                                value={form.user_uuid}
                                onChange={(event) => setForm((prev) => ({ ...prev, user_uuid: event.target.value }))}
                            >
                                {userOptions.map((user) => (
                                    <MenuItem key={user.uuid} value={user.uuid}>
                                        {applyName(user.name) || applyEmail(user.email) || user.uuid}
                                        {user.email && user.name ? ` (${applyEmail(user.email)})` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Motivo (opcional)"
                            value={form.reason}
                            onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                        <TextField
                            label="Válida até (opcional)"
                            type="date"
                            value={form.exempt_until}
                            onChange={(event) => setForm((prev) => ({ ...prev, exempt_until: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            helperText="Deixe em branco para isenção por tempo indeterminado."
                            fullWidth
                        />
                        <Box>
                            <Button type="submit" variant="contained" disabled={saving}>
                                {saving ? 'Salvando…' : 'Adicionar isenção'}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Isenções ativas
                </Typography>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : exemptions.length === 0 ? (
                    <Alert severity="info">Nenhum usuário isento no momento.</Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Usuário</TableCell>
                                    <TableCell>Motivo</TableCell>
                                    <TableCell>Válida até</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exemptions.map((exemption) => (
                                    <TableRow key={exemption.uuid} hover>
                                        <TableCell>
                                            <Typography variant="body2">{exemption.user_name || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {exemption.user_email || exemption.user_uuid}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{exemption.reason || '—'}</TableCell>
                                        <TableCell>
                                            {exemption.indefinite ? (
                                                <Chip label="Indeterminado" size="small" color="default" />
                                            ) : (
                                                formatDate(exemption.exempt_until)
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                color="warning"
                                                onClick={() => handleRevoke(exemption)}
                                            >
                                                Encerrar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}

export default SubscriptionBillingExemptionsPage;
